import { CalendarRange } from "@/components/CalendarRange";
import { CalendarSingle } from "@/components/CalendarSingle";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { FilterType } from "@/lib/filters";
import { cn } from "@/lib/utils";
import { FilterValue } from "@/types/dataTableSettings";
import { CalendarIcon, X } from "lucide-react";

import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import { useFilterState } from "../hooks/useFilterState";
import { ClearFilterButton } from "./ClearFilterButton";
import { ClearFilterIconButton } from "./ClearFilterIconButton";
import { FilterTypeOptions } from "./FilterTypeOptions";
import { FilterTypeIcon } from "./FilterTypeIcon";
import { formatDate } from "@/lib/utils/formatters";
import { ColumnFiltersState } from "@tanstack/react-table";

interface DateFilterProps {
  columnId: string;
}

const MODE_SINGLE = "single";
const MODE_RANGE = "range";

type DateFilterMode = typeof MODE_SINGLE | typeof MODE_RANGE;

const getFilterByType = (
  columnFilters: ColumnFiltersState,
  filterType: FilterType,
) => columnFilters.find((f) => (f.value as FilterValue).filter === filterType);

const hasFilterOfType = (
  columnFilters: ColumnFiltersState,
  filterType: FilterType,
) => columnFilters.some((f) => (f.value as FilterValue).filter === filterType);

export const DateFilter = ({ columnId }: DateFilterProps) => {
  const { t } = useTranslation();
  const { options } = useDataTableSettings();

  const {
    filterType,
    onFilterTypeChange,
    readOnly,
    isFilteredOrSorted,
    columnFilterValue,
    clearFilter: clearSingleFilter,
  } = useFilterState({
    columnId,
    defaultFilterType: FilterType.GreaterThanOrEqual,
  });

  const columnFilters = useMemo(
    () => options.rawFilters.filter((f) => f.id === columnId),
    [options, columnId],
  );

  // Detect initial mode from persisted filters
  const initialMode = useMemo((): DateFilterMode => {
    const hasGte = hasFilterOfType(
      columnFilters,
      FilterType.GreaterThanOrEqual,
    );
    const hasLte = hasFilterOfType(columnFilters, FilterType.LessThanOrEqual);
    return hasGte && hasLte ? MODE_RANGE : MODE_SINGLE;
  }, [columnFilters]);

  const [mode, setMode] = useState<DateFilterMode>(initialMode);
  const [open, setOpen] = useState(false);

  const isSingleMode = mode === MODE_SINGLE;
  const isRangeMode = mode === MODE_RANGE;

  // Single mode date value
  const singleDateValue = useMemo(() => {
    if (!columnFilterValue?.value) return undefined;
    const dt = DateTime.fromFormat(
      columnFilterValue.value,
      DEFAULT_DATE_FORMAT,
    );
    return dt.isValid ? dt.toJSDate() : undefined;
  }, [columnFilterValue?.value]);

  // Range mode date values
  const rangeValue = useMemo((): DateRange | undefined => {
    const gteFilter = getFilterByType(
      columnFilters,
      FilterType.GreaterThanOrEqual,
    );
    const lteFilter = getFilterByType(
      columnFilters,
      FilterType.LessThanOrEqual,
    );

    const fromValue = gteFilter
      ? (gteFilter.value as FilterValue).value
      : undefined;
    const toValue = lteFilter
      ? (lteFilter.value as FilterValue).value
      : undefined;

    if (!fromValue && !toValue) return undefined;

    const fromDate = fromValue
      ? DateTime.fromFormat(fromValue, DEFAULT_DATE_FORMAT).toJSDate()
      : undefined;
    const toDate = toValue
      ? DateTime.fromFormat(toValue, DEFAULT_DATE_FORMAT).toJSDate()
      : undefined;

    return { from: fromDate, to: toDate };
  }, [columnFilters]);

  const handleModeChange = useCallback((checked: boolean) => {
    const newMode = checked ? MODE_RANGE : MODE_SINGLE;
    setMode(newMode);
  }, []);

  const onSingleDateSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        options.resetFilterById(columnId);
        options.setFilterValue(columnId, {
          value: formatDate(date),
          filter: filterType,
        });
        options.setFilteredMark(columnId, true);
      }
    },
    [filterType, options, columnId],
  );

  const onRangeSelect = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from && range?.to) {
        const fromDate = range.from;
        const toDate = range.to;
        options.setFilters((prev) => {
          const filtered = prev.filter((f) => f.id !== columnId);
          filtered.push({
            id: columnId,
            value: {
              value: formatDate(fromDate),
              filter: FilterType.GreaterThanOrEqual,
            },
          });
          filtered.push({
            id: columnId,
            value: {
              value: formatDate(toDate),
              filter: FilterType.LessThanOrEqual,
            },
          });
          return filtered;
        });
        options.setFilteredMark(columnId, true);
      }
    },
    [columnId, options],
  );

  const clearFilter = useCallback(() => {
    if (mode === MODE_SINGLE) {
      clearSingleFilter();
    } else {
      options.setFilters((prev) => prev.filter((f) => f.id !== columnId));
      options.setFilteredMark(columnId, false);
    }
  }, [mode, clearSingleFilter, options, columnId]);

  const confirmSelection = useCallback(() => {
    setOpen(false);
  }, []);

  const hasValue = useMemo(() => {
    if (mode === MODE_SINGLE) {
      return (
        columnFilterValue?.value !== undefined && columnFilterValue.value !== ""
      );
    }
    return rangeValue?.from !== undefined || rangeValue?.to !== undefined;
  }, [mode, columnFilterValue?.value, rangeValue]);

  // Format trigger label
  const triggerLabel = useMemo(() => {
    if (mode === MODE_SINGLE) {
      if (filterType === FilterType.IsEmpty) {
        return t("table.filters.isEmpty");
      }
      if (filterType === FilterType.IsNotEmpty) {
        return t("table.filters.isNotEmpty");
      }
      return singleDateValue
        ? formatDate(singleDateValue)
        : t("datePicker.pickADate");
    }
    if (rangeValue?.from && rangeValue?.to) {
      return (
        <div className="flex flex-col text-[11px]/4 @4xs:flex-row @4xs:text-sm @4xs:gap-1">
          <span>{formatDate(rangeValue.from)}</span>
          <span className="hidden @4xs:inline">-</span>
          <span>{formatDate(rangeValue.to)}</span>
        </div>
      );
    }
    if (rangeValue?.from) {
      return `${formatDate(rangeValue.from)} - ...`;
    }
    return t("datePicker.pickRange");
  }, [mode, filterType, singleDateValue, rangeValue, t]);

  return (
    <div className="flex gap-1 items-center min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "@container h-10 w-full min-w-0 justify-start text-left font-normal truncate",
              !hasValue && "text-muted-foreground",
              isFilteredOrSorted && "border-blue-500/50",
            )}
          >
            {isRangeMode ? (
              <CalendarIcon className="h-4 w-4 hidden @5xs:inline" />
            ) : (
              <FilterTypeIcon
                filterType={filterType}
                className="h-4 w-4 hidden @5xs:inline"
              />
            )}
            {triggerLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex">
            <div className="p-3 flex flex-col gap-3">
              <div className="flex items-center gap-2 h-8">
                <Switch
                  id="date-filter-mode"
                  checked={isRangeMode}
                  onCheckedChange={handleModeChange}
                />
                <Label htmlFor="date-filter-mode" className="text-sm">
                  {t("datePicker.range")}
                </Label>
              </div>
              <div className="h-px bg-border" />
              <FilterTypeOptions
                filterType={filterType}
                onChange={onFilterTypeChange}
                className={cn(isRangeMode && "opacity-50 pointer-events-none")}
              />
            </div>
            <div className="p-3 flex flex-col gap-3">
              <header className="h-8 flex justify-end items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </header>
              <div className="p-1 border border-slate-200 rounded">
                {isSingleMode ? (
                  <CalendarSingle
                    selected={singleDateValue}
                    onSelect={onSingleDateSelect}
                  />
                ) : (
                  <CalendarRange
                    selected={rangeValue}
                    onSelect={onRangeSelect}
                  />
                )}
              </div>

              <footer className="flex justify-end gap-2">
                <ClearFilterButton
                  onClick={clearFilter}
                  visible={hasValue && !(readOnly && isSingleMode)}
                />
                <Button disabled={!hasValue} onClick={confirmSelection}>
                  {t("datePicker.select")}
                </Button>
              </footer>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <ClearFilterIconButton
        onClick={clearFilter}
        visible={hasValue && !(readOnly && isSingleMode)}
      />
    </div>
  );
};
